import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { ThemeContext, Theme, AccentColor } from '../lib/theme';

const themeColors = {
  indigo: {
    primary: '99 102 241',
    dark: '79 70 229', 
    light: '129 140 248'
  },
  blue: {
    primary: '37 99 235',
    dark: '29 78 216',
    light: '59 130 246'
  },
  purple: {
    primary: '147 51 234',
    dark: '126 34 206',
    light: '168 85 247'
  },
  green: {
    primary: '22 163 74',
    dark: '21 128 61',
    light: '34 197 94'
  },
  rose: {
    primary: '225 29 72',
    dark: '190 18 60',
    light: '244 63 94'
  },
  amber: {
    primary: '217 119 6',
    dark: '180 83 9',
    light: '245 158 11'
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as Theme) || 'light';
  });
  
  const [accentColor, setAccentColor] = useState<AccentColor>(() => {
    const saved = localStorage.getItem('accentColor');
    return (saved as AccentColor) || 'indigo';
  });

  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        // First check if we have a session
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (!sessionData.session) {
          // No active session, use default preferences
          return;
        }

        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('Error getting user:', userError);
          return;
        }
        
        if (!user) {
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('theme_color')
          .eq('id', user.id)
          .maybeSingle();
            
        if (profileError) {
          console.error('Error fetching profile:', profileError);
          return;
        }

        if (profile?.theme_color) {
          handleAccentColorChange(profile.theme_color as AccentColor, false);
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
      }
    };
    
    loadUserPreferences();
  }, []);

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    const colors = themeColors[accentColor];

    root.style.setProperty('--theme-color', colors.primary);
    root.style.setProperty('--theme-color-dark', colors.dark);
    root.style.setProperty('--theme-color-light', colors.light);

    localStorage.setItem('accentColor', accentColor);
  }, [accentColor]);

  const handleAccentColorChange = async (color: AccentColor, saveToProfile = true) => {
    setAccentColor(color);
    
    if (!saveToProfile) return;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        // No active session, just save to localStorage
        localStorage.setItem('accentColor', color);
        return;
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting user:', userError);
        localStorage.setItem('accentColor', color);
        return;
      }
      
      if (!user) {
        localStorage.setItem('accentColor', color);
        return;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ theme_color: color })
        .eq('id', user.id);

      if (updateError) {
        console.error('Error updating profile:', updateError);
        localStorage.setItem('accentColor', color);
      }
      
    } catch (error) {
      console.error('Error saving accent color:', error);
      // Still keep the local state updated even if server sync fails
      localStorage.setItem('accentColor', color);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      accentColor, 
      setAccentColor: handleAccentColorChange 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};